import { Injectable } from '@angular/core';
import { Question } from '../../model/question';
import { Subject as SubjectModel } from '../../model/subject';
import { LoggerService } from '../utils/logger.service';
import type { Id2Question, RecreateSettings } from './rest.types';

export type UserTestSettingsCache = {
  shuffleQuestionAnswers: boolean;
  randomizeQuestions: boolean;
  nextAutomatically: boolean;
};

/**
 * In-memory study/exam session state (question order, loaded questions, current test id).
 * Extracted from RestService for clearer boundaries and future offline/sync work.
 */
@Injectable({ providedIn: 'root' })
export class StudySessionStore {
  timeProgressExam = 0;
  questionIds: number[] = [];
  questionsMap: Id2Question = {};
  courseId = 0;
  subjectIds: number[] = [];
  currentUserTestId: number | null = null;
  currentUserTestMode: string | null = null;
  currentExamTimeMinutes: number | null = null;
  questionIdsLoadedForTest: number | null = null;
  subjects: SubjectModel[] = [];
  currentSubjects: string[] = [];

  private _maxQuestions = 0;
  private userTestSettingsCache: UserTestSettingsCache | null = null;
  private recreateSettings: RecreateSettings | null = null;
  private questionLoadLocked = false;

  get maxQuestions(): number {
    return this._maxQuestions;
  }

  set maxQuestions(value: number) {
    this._maxQuestions = value;
  }

  get isQuestionLoadLocked(): boolean {
    return this.questionLoadLocked;
  }

  async acquireQuestionLoadLock(): Promise<void> {
    while (this.questionLoadLocked) {
      await this.wait(100);
    }
    this.questionLoadLocked = true;
  }

  releaseQuestionLoadLock(): void {
    this.questionLoadLocked = false;
  }

  shuffleArray(array: number[]): number[] {
    for (let i = array.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [array[i], array[j]] = [array[j], array[i]];
    }
    return array;
  }

  getQuestionIds(): number[] {
    // Active test: questionIds is the exact test list — do not apply the pool
    // picker cap (_maxQuestions), which may still reflect exam mode (e.g. 320).
    if (
      this.currentUserTestId != null &&
      this.questionIdsLoadedForTest === this.currentUserTestId &&
      this.questionIds.length > 0
    ) {
      return [...this.questionIds];
    }
    const limitedIds: number[] = [];
    for (let i = 0; i < this.questionIds.length && i < this._maxQuestions; i++) {
      limitedIds[i] = this.questionIds[i];
    }
    return limitedIds;
  }

  getQuestionsMap(): Id2Question {
    return this.questionsMap;
  }

  setQuestionIdsForResume(questionIds: number[]): void {
    this.questionIds = questionIds;
    this._maxQuestions = questionIds.length;
    this.questionIdsLoadedForTest = this.currentUserTestId;
    console.log(
      '[setQuestionIdsForResume] Set',
      questionIds.length,
      'questionIds for test',
      this.currentUserTestId,
    );
    this.questionsMap = {};
    let prevId: number | null = null;
    questionIds.forEach((id, index) => {
      const nextId = index < questionIds.length - 1 ? questionIds[index + 1] : null;
      this.questionsMap[id] = new Question(id, '', '', null, prevId, true, '', []);
      if (this.questionsMap[id]) {
        this.questionsMap[id]!.nextId = nextId;
      }
      prevId = id;
    });
  }

  findFirstQuestion(): Question | null {
    for (const key of Object.keys(this.questionsMap)) {
      const question = this.questionsMap[+key];
      if (question && question.prevId === null) {
        return question;
      }
    }
    return null;
  }

  reset(): void {
    console.log('[reset] Resetting questions map. currentUserTestId before reset:', this.currentUserTestId);
    console.log('[reset] questionIds before reset:', this.questionIds);
    console.log('[reset] questionsMap keys before reset:', Object.keys(this.questionsMap));
    this.questionIds = [];
    this.questionsMap = {};
    console.log('[reset] After reset - questionIds:', this.questionIds, 'questionsMap keys:', Object.keys(this.questionsMap));
    console.log('[reset] After reset, currentUserTestId:', this.currentUserTestId);
  }

  clearCurrentTestState(): void {
    LoggerService.log('[clearCurrentTestState] Clearing all current test state.');
    this.questionIds = [];
    this.questionsMap = {};
    this.currentUserTestId = null;
    this.currentUserTestMode = null;
    this.currentExamTimeMinutes = null;
    this._maxQuestions = 0;
    this.currentSubjects = [];
    this.questionIdsLoadedForTest = null;
    this.clearUserTestSettingsCache();
  }

  getRecreateSettings(): RecreateSettings | null {
    return this.recreateSettings;
  }

  setRecreateSettings(settings: RecreateSettings): void {
    this.recreateSettings = settings;
  }

  clearRecreateSettings(): void {
    this.recreateSettings = null;
  }

  getFirstQuestionId(): number | null {
    if (this.questionIds.length > 0) {
      return this.questionIds[0];
    }
    return null;
  }

  updateAnswers(questionFresh: Question, _correct: boolean): void {
    for (const key of Object.keys(this.questionsMap)) {
      const question = this.questionsMap[+key];
      if (question && question.id === questionFresh.id) {
        this.questionsMap[+key] = questionFresh;
        LoggerService.log('Question updated: ' + key);
      }
    }
  }

  getUserTestSettingsCache(): UserTestSettingsCache | null {
    return this.userTestSettingsCache;
  }

  setUserTestSettingsCache(cache: UserTestSettingsCache): void {
    this.userTestSettingsCache = cache;
  }

  clearUserTestSettingsCache(): void {
    this.userTestSettingsCache = null;
  }

  private wait(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
}
